export const marketingStats = [
  { value: "2–4 weeks", label: "to a testable first release" },
  { value: "Senior-led", label: "strategy, design and engineering" },
  { value: "Accessible", label: "responsive, keyboard-friendly builds" },
  { value: "Launch +", label: "measured support after go-live" },
];

export const servicesFallback = [
  {
    service_id: "svc-product",
    slug: "product-design",
    title: "Product & UX design",
    desc: "Research, journeys, wireframes and a practical design system—built around the decision you need users to make.",
    icon: "PanelsTopLeft",
    starts_at: 24000,
    currency: "INR",
    deliverables: ["UX audit and positioning", "Responsive interface design", "Clickable prototype"],
  },
  {
    service_id: "svc-web",
    slug: "web-development",
    title: "Web design & development",
    desc: "Fast, responsive marketing sites and web applications engineered for clarity, conversion and maintainability.",
    icon: "Code2",
    starts_at: 48000,
    currency: "INR",
    deliverables: ["Frontend and backend build", "CMS and integrations", "Performance and SEO foundations"],
  },
  {
    service_id: "svc-commerce",
    slug: "commerce",
    title: "Commerce experiences",
    desc: "Product discovery, checkout and operations flows that remove friction without hiding the commercial details.",
    icon: "ShoppingBag",
    starts_at: 64000,
    currency: "INR",
    deliverables: ["Storefront experience", "Payments and fulfilment", "Analytics instrumentation"],
  },
  {
    service_id: "svc-integrations",
    slug: "integrations",
    title: "Systems & integrations",
    desc: "Payments, authentication, CRM, automation and APIs connected into one dependable customer journey.",
    icon: "Workflow",
    starts_at: 16000,
    currency: "INR",
    deliverables: ["Integration architecture", "Secure implementation", "Operational documentation"],
  },
  {
    service_id: "svc-optimization",
    slug: "optimization",
    title: "Performance & conversion",
    desc: "Focused improvements for an existing product: Core Web Vitals, accessibility, messaging and funnel friction.",
    icon: "Gauge",
    starts_at: 18000,
    currency: "INR",
    deliverables: ["Technical and UX audit", "Prioritised fixes", "Before-and-after measurement"],
  },
  {
    service_id: "svc-care",
    slug: "care",
    title: "Product care",
    desc: "A steady senior partner for releases, monitoring and incremental improvements after the first launch.",
    icon: "LifeBuoy",
    starts_at: 12000,
    currency: "INR",
    deliverables: ["Release support", "Monitoring and maintenance", "Monthly improvement plan"],
  },
];

const images = {
  lumen: "https://images.unsplash.com/photo-1587397845856-e6cf49176c70?auto=format&fit=crop&w=1600&q=82",
  kanvas: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1600&q=82",
  orbital: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=82",
  voltage: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1600&q=82",
  neon: "https://images.unsplash.com/photo-1523726491678-bf852e717f6a?auto=format&fit=crop&w=1600&q=82",
  signal: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=82",
};

const fitnessProjects = [
  {
    item_id: "absolute-fitness-studio",
    title: "Absolute Fitness Studio",
    category: "local-business",
    style: "editorial",
    industry: "Fitness & wellness",
    stack: ["React", "Responsive design", "Local SEO"],
    cover: "/portfolio/absolute-fitness-studio.png",
    demo: "https://absolute-studio.netlify.app/",
    summary: "An editorial, review-led neighbourhood fitness site that turns local proof into studio visits.",
    client: "Absolute Fitness Studio, Bijoygarh",
    role: "Strategy, UX, visual design and development",
    duration: "Launch project",
    problem: "The studio needed a website that could communicate its atmosphere, equipment and trusted local reputation before a prospective member visited in person.",
    process: [
      { title: "Map visit intent", detail: "The information architecture follows the questions a prospective member asks before travelling: what the space feels like, what is available and how to visit." },
      { title: "Build an editorial identity", detail: "A warm neutral canvas, high-contrast typography and disciplined red accents give the studio a confident identity distinct from template fitness sites." },
      { title: "Lead with real proof", detail: "Studio photography, rating context and member reviews establish trust without interrupting the main journey." },
      { title: "Make action effortless", detail: "Calls, directions and visit enquiries stay visible across desktop and mobile, with clear accessibility and reduced-motion considerations." },
    ],
    results: [
      { metric: "Live", label: "production website" },
      { metric: "4.7", label: "member rating featured" },
      { metric: "276", label: "reviews brought into the journey" },
      { metric: "Mobile", label: "responsive visit flow" },
    ],
    gallery: ["/portfolio/absolute-fitness-studio.png"],
  },
  {
    item_id: "fitnet-health-paradise",
    title: "FitNet Health Paradise",
    category: "local-business",
    style: "friendly",
    industry: "Fitness & wellness",
    stack: ["React", "Responsive design", "Local SEO"],
    cover: "/portfolio/fitnet-health-paradise.png",
    demo: "https://fitnet-paradise.netlify.app/",
    summary: "A friendly, community-first fitness website designed to make starting feel practical and approachable.",
    client: "FitNet Health Paradise, Bijoygarh",
    role: "Strategy, UX, visual design and development",
    duration: "Launch project",
    problem: "FitNet needed to translate a strong neighbourhood reputation into a clear digital experience for beginners, returning members and goal-led prospects.",
    process: [
      { title: "Start with reassurance", detail: "The message and page structure reduce the intimidation that often stops first-time members from making contact." },
      { title: "Create a community palette", detail: "Deep navy, fresh green and rounded visual forms communicate energy while keeping the experience welcoming." },
      { title: "Turn goals into a pathway", detail: "An interactive priority chooser helps visitors frame their enquiry around strength, energy, weight management or restarting." },
      { title: "Connect local proof", detail: "Real studio imagery, member feedback, directions, phone and Instagram are integrated into one practical journey." },
    ],
    results: [
      { metric: "Live", label: "production website" },
      { metric: "4.6", label: "member rating featured" },
      { metric: "895", label: "reviews brought into the journey" },
      { metric: "4", label: "goal-led enquiry pathways" },
    ],
    gallery: ["/portfolio/fitnet-health-paradise.png"],
  },
  {
    item_id: "androgen-fitness-studio",
    title: "Androgen Fitness Studio",
    category: "local-business",
    style: "performance",
    industry: "Fitness & wellness",
    stack: ["React", "Responsive design", "Local SEO"],
    cover: "/portfolio/androgen-fitness-studio.png",
    demo: "https://androgen-fitness.netlify.app/",
    summary: "A performance-led local fitness site with a bold identity and a direct path from interest to enquiry.",
    client: "Androgen – The Fitness Studio, Baghajatin",
    role: "Strategy, UX, visual design and development",
    duration: "Launch project",
    problem: "The studio needed a stronger digital first impression—one that reflected the training-floor energy while still answering practical membership questions clearly.",
    process: [
      { title: "Define the attitude", detail: "A direct ‘put in the work’ proposition anchors the experience in commitment rather than generic fitness promises." },
      { title: "Design for impact", detail: "A black, white and electric-blue system pairs oversized type with real training-floor imagery for immediate recognition." },
      { title: "Structure the decision", detail: "The journey moves from equipment and atmosphere to a simple three-step path: share a goal, inspect the space and choose the next step." },
      { title: "Keep local actions close", detail: "Phone, directions, review proof and enquiry actions are available where intent is highest on every screen size." },
    ],
    results: [
      { metric: "Live", label: "production website" },
      { metric: "4.7", label: "member rating featured" },
      { metric: "279", label: "reviews brought into the journey" },
      { metric: "3", label: "clear enquiry steps" },
    ],
    gallery: ["/portfolio/androgen-fitness-studio.png"],
  },
  {
    item_id: "encore-fitness-studio",
    title: "Encore Fitness Studio",
    category: "local-business",
    style: "premium",
    industry: "Fitness & wellness",
    stack: ["React", "Responsive design", "Local SEO"],
    cover: "/portfolio/encore-fitness-studio.png",
    demo: "https://encore-fitness.netlify.app/",
    summary: "A warm, premium studio website that balances personal encouragement with credible local proof.",
    client: "Encore Fitness Studio, Tollygunge",
    role: "Strategy, UX, visual design and development",
    duration: "Launch project",
    problem: "Encore needed a website that felt personal and supportive without losing the confidence, equipment detail and local trust expected from an established studio.",
    process: [
      { title: "Shape a personal promise", detail: "The core story frames training as the start of a stronger chapter, giving the site a human tone from the first screen." },
      { title: "Build a premium visual system", detail: "Editorial typography, warm neutrals, burgundy accents and arched photography create a distinctive but approachable identity." },
      { title: "Show the real experience", detail: "The equipment floor, trainer support and member voice are presented as concrete reasons to visit—not decorative claims." },
      { title: "Design the visit journey", detail: "Calls to plan a visit, phone the studio, open directions and view social proof remain clear across responsive breakpoints." },
    ],
    results: [
      { metric: "Live", label: "production website" },
      { metric: "4.7", label: "member rating featured" },
      { metric: "309", label: "reviews brought into the journey" },
      { metric: "3", label: "training pillars clarified" },
    ],
    gallery: ["/portfolio/encore-fitness-studio.png"],
  },
];

export const portfolioFallback = [
  ...fitnessProjects,
  {
    item_id: "lumen-studio",
    title: "Lumen Studio",
    category: "portfolio",
    style: "minimalist",
    industry: "Creative services",
    stack: ["React", "Sanity", "Motion"],
    cover: images.lumen,
    summary: "An editorial portfolio that lets the work lead and makes the next step unmistakable.",
    client: "Lumen Studio",
    role: "Strategy, design and build",
    duration: "6 weeks",
    problem: "The studio had strong work but a weak path from browsing to enquiry. The redesign turned the portfolio into a concise sales narrative instead of a gallery with no direction.",
    process: [
      { title: "Clarify the story", detail: "We reduced the proposition to the proof clients actually needed before making contact." },
      { title: "Design the system", detail: "A restrained editorial system gave every project a consistent rhythm without flattening its personality." },
      { title: "Build for speed", detail: "Responsive media, deliberate motion and a simple publishing workflow kept the experience fast and maintainable." },
      { title: "Make conversion visible", detail: "Contextual calls to action now appear where intent is highest, without interrupting the work." },
    ],
    results: [
      { metric: "–58%", label: "bounce rate" },
      { metric: "3.4×", label: "time on site" },
      { metric: "+41%", label: "qualified enquiries" },
      { metric: "94", label: "performance score" },
    ],
    gallery: [images.lumen, images.neon, images.voltage],
  },
  {
    item_id: "kanvas-commerce",
    title: "Kanvas Commerce",
    category: "e-commerce",
    style: "bold",
    industry: "Retail",
    stack: ["Shopify", "React", "Analytics"],
    cover: images.kanvas,
    summary: "A mobile-first buying journey that brings product, confidence and checkout into one clean flow.",
    client: "Kanvas",
    role: "Experience and engineering",
    duration: "9 weeks",
    problem: "Mobile demand was healthy, but checkout friction and weak product hierarchy were costing sales. The new journey prioritised decision-making speed and payment confidence.",
    process: [
      { title: "Read the funnel", detail: "Analytics and session patterns exposed the two steps creating most abandonment." },
      { title: "Reframe discovery", detail: "Product benefits, proof and variants were reordered around real buying questions." },
      { title: "Simplify checkout", detail: "Express payment and address assistance moved forward, while distractions were removed." },
      { title: "Instrument the release", detail: "The team shipped with event tracking so every improvement could be measured." },
    ],
    results: [
      { metric: "+38%", label: "mobile conversion" },
      { metric: "–51%", label: "checkout abandonment" },
      { metric: "1.1s", label: "interactive on 4G" },
      { metric: "+27%", label: "repeat purchase rate" },
    ],
    gallery: [images.kanvas, images.signal, images.orbital],
  },
  {
    item_id: "orbital-ops",
    title: "Orbital Ops",
    category: "saas",
    style: "dark",
    industry: "B2B software",
    stack: ["React", "FastAPI", "MongoDB"],
    cover: images.orbital,
    summary: "A dense operations product redesigned so teams can understand risk and act in seconds.",
    client: "Orbital Ops",
    role: "Product design and full-stack",
    duration: "14 weeks",
    problem: "Critical information lived behind several layers of navigation. During incidents, teams relied on colleagues instead of the product. We rebuilt the hierarchy around the first decisions operators make.",
    process: [
      { title: "Observe real work", detail: "Incident workflows revealed which signals mattered first and what could safely wait." },
      { title: "Model the interface", detail: "Saved views, clearer severity and a consistent command structure reduced cognitive load." },
      { title: "Engineer realtime", detail: "A resilient event layer kept operational views current without overwhelming the interface." },
      { title: "Test under pressure", detail: "Scenario testing validated navigation, language and recovery paths before launch." },
    ],
    results: [
      { metric: "–63%", label: "time to resolution" },
      { metric: "2.1×", label: "daily active users" },
      { metric: "<120ms", label: "p99 event latency" },
      { metric: "AA", label: "accessibility target" },
    ],
    gallery: [images.orbital, images.voltage, images.signal],
  },
  {
    item_id: "voltage-capital",
    title: "Voltage Capital",
    category: "corporate",
    style: "minimalist",
    industry: "Financial services",
    stack: ["React", "Contentful", "SEO"],
    cover: images.voltage,
    summary: "A credible investor experience that turns a complex proposition into a clear point of view.",
  },
  {
    item_id: "neon-journal",
    title: "Neon Journal",
    category: "blogs",
    style: "vibrant",
    industry: "Media",
    stack: ["Astro", "MDX", "Stripe"],
    cover: images.neon,
    summary: "A flexible editorial system designed for long reads, rich media and paid membership.",
  },
  {
    item_id: "signal-labs",
    title: "Signal Labs",
    category: "landing",
    style: "bold",
    industry: "Technology",
    stack: ["React", "Experiments", "Analytics"],
    cover: images.signal,
    summary: "A focused launch experience that makes an unfamiliar product easy to understand and act on.",
  },
].map((item, index, all) => ({
  ...item,
  demo: item.demo || "#",
  gallery: item.gallery || [item.cover],
  related: item.related || all.filter((candidate) => candidate.item_id !== item.item_id).slice(0, 3).map((candidate) => ({
    item_id: candidate.item_id,
    title: candidate.title,
    category: candidate.category,
    style: candidate.style,
    cover: candidate.cover,
  })),
}));

export function findFallbackCase(itemId) {
  return portfolioFallback.find((item) => item.item_id === itemId);
}
